import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, Sparkles, Tag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ProductCard from '../components/ProductCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SalePage = () => {
  const { t, getLocalizedText, language } = useLanguage();
  const [sortBy, setSortBy] = useState('default');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products/sale`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const sortedProducts = useMemo(() => {
    let filtered = [...products];

    switch (sortBy) {
      case 'price-asc':
        return filtered.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price));
      case 'price-desc':
        return filtered.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price));
      case 'name-asc':
        return filtered.sort((a, b) => 
          getLocalizedText(a.name).localeCompare(getLocalizedText(b.name))
        );
      case 'name-desc':
        return filtered.sort((a, b) => 
          getLocalizedText(b.name).localeCompare(getLocalizedText(a.name))
        );
      default:
        return filtered;
    }
  }, [sortBy, getLocalizedText, products]);

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="container mx-auto">
        {/* Header with special styling */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-yellow-400 via-orange-400 to-red-500 rounded-xl">
              <Sparkles className="h-8 w-8 text-black" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-yellow-400 via-orange-400 to-red-500 bg-clip-text text-transparent">
              {t.nav.sale}
            </h1>
          </div>
          <p className="text-zinc-400">
            {language === 'hu' 
              ? 'Különleges ajánlatok és kedvezményes termékek!' 
              : 'Special offers and discounted products!'}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8 p-4 bg-zinc-900 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">{t.filters.sortBy}:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="default" className="text-white hover:bg-zinc-700">{t.filters.all}</SelectItem>
                <SelectItem value="price-asc" className="text-white hover:bg-zinc-700">{t.filters.priceAsc}</SelectItem>
                <SelectItem value="price-desc" className="text-white hover:bg-zinc-700">{t.filters.priceDesc}</SelectItem>
                <SelectItem value="name-asc" className="text-white hover:bg-zinc-700">{t.filters.nameAsc}</SelectItem>
                <SelectItem value="name-desc" className="text-white hover:bg-zinc-700">{t.filters.nameDesc}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-zinc-500 mx-auto mb-4" />
            <p className="text-zinc-500">{t.common.loading}</p>
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className="text-center py-20">
            <Tag className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 text-xl mb-2">
              {language === 'hu' 
                ? 'Jelenleg nincsenek akciós termékek' 
                : 'No sale products available at the moment'}
            </p>
            <p className="text-zinc-500">
              {language === 'hu' 
                ? 'Nézzen vissza később!' 
                : 'Check back later!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
            {sortedProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalePage;
